import React from 'react';
import { Shield, Lock, Eye, Database, UserCheck, AlertCircle } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/Card';

const PrivacyPolicy = () => {
  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center py-8">
        <div className="w-16 h-16 bg-reddit-blue rounded-full flex items-center justify-center mx-auto mb-4">
          <Shield className="w-8 h-8 text-white" />
        </div>
        <h1 className="text-4xl font-bold mb-4">Privacy Policy</h1>
        <p className="text-lg text-reddit-600 dark:text-dark-textMuted max-w-3xl mx-auto">
          Your privacy is important to us. This policy outlines how we collect, use, and protect your information.
        </p>
        <p className="text-sm text-reddit-600 dark:text-dark-textMuted mt-2">
          Last Updated: January 27, 2026
        </p>
      </div>

      {/* Overview */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Eye className="w-6 h-6 text-reddit-orange" />
            Privacy Overview
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="mb-4">
            Trendy is committed to protecting your privacy and ensuring you have a positive experience on our website. 
            This policy outlines our practices regarding the collection, use, and disclosure of your information.
          </p>
          <div className="bg-reddit-50 dark:bg-dark-border p-4 rounded-lg">
            <h4 className="font-semibold mb-2">Key Points</h4>
            <ul className="list-disc list-inside space-y-1 text-sm">
              <li>We do not sell your personal information</li>
              <li>We use minimal data collection practices</li>
              <li>Your data is stored locally in your browser</li>
              <li>We do not track your browsing across other websites</li>
              <li>You have full control over your data</li>
            </ul>
          </div>
        </CardContent>
      </Card>

      {/* Information We Collect */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Database className="w-6 h-6 text-reddit-blue" />
            Information We Collect
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <h4 className="font-semibold mb-2">1. Information You Provide</h4>
            <p className="text-sm text-reddit-600 dark:text-dark-textMuted mb-2">
              We collect information that you voluntarily provide when using Trendy:
            </p>
            <ul className="list-disc list-inside space-y-1 text-sm pl-4">
              <li>Preferences and settings you configure</li>
              <li>Favorites and saved trends</li>
              <li>Search queries within the application</li>
              <li>Theme preferences (light/dark mode)</li>
            </ul>
          </div>

          <div>
            <h4 className="font-semibold mb-2">2. Automatically Collected Information</h4>
            <p className="text-sm text-reddit-600 dark:text-dark-textMuted mb-2">
              We automatically collect certain information when you use Trendy:
            </p>
            <ul className="list-disc list-inside space-y-1 text-sm pl-4">
              <li>Browser type and version</li>
              <li>Device information</li>
              <li>Usage patterns and interactions</li>
              <li>Error logs for debugging purposes</li>
            </ul>
          </div>

          <div>
            <h4 className="font-semibold mb-2">3. Local Storage</h4>
            <p className="text-sm text-reddit-600 dark:text-dark-textMuted">
              Most of your data is stored locally in your browser using localStorage. This means your preferences, 
              favorites, and settings remain on your device and are not transmitted to our servers.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* How We Use Information */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <UserCheck className="w-6 h-6 text-reddit-green" />
            How We Use Your Information
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p>We use the collected information for the following purposes:</p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-reddit-50 dark:bg-dark-border p-4 rounded-lg">
              <h4 className="font-semibold mb-2">Service Provision</h4>
              <ul className="list-disc list-inside space-y-1 text-sm">
                <li>Display trending topics</li>
                <li>Personalize your experience</li>
                <li>Save your preferences</li>
                <li>Provide search functionality</li>
              </ul>
            </div>

            <div className="bg-reddit-50 dark:bg-dark-border p-4 rounded-lg">
              <h4 className="font-semibold mb-2">Improvement</h4>
              <ul className="list-disc list-inside space-y-1 text-sm">
                <li>Analyze usage patterns</li>
                <li>Fix bugs and errors</li>
                <li>Enhance features</li>
                <li>Improve performance</li>
              </ul>
            </div>

            <div className="bg-reddit-50 dark:bg-dark-border p-4 rounded-lg">
              <h4 className="font-semibold mb-2">Communication</h4>
              <ul className="list-disc list-inside space-y-1 text-sm">
                <li>Send important updates</li>
                <li>Respond to inquiries</li>
                <li>Notify about changes</li>
                <li>Provide support</li>
              </ul>
            </div>

            <div className="bg-reddit-50 dark:bg-dark-border p-4 rounded-lg">
              <h4 className="font-semibold mb-2">Security</h4>
              <ul className="list-disc list-inside space-y-1 text-sm">
                <li>Prevent fraud</li>
                <li>Protect against abuse</li>
                <li>Monitor security threats</li>
                <li>Ensure data integrity</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Data Storage and Security */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Lock className="w-6 h-6 text-reddit-orange" />
            Data Storage and Security
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <h4 className="font-semibold mb-2">Local Storage</h4>
            <p className="text-sm text-reddit-600 dark:text-dark-textMuted">
              Your preferences, favorites, and settings are stored locally in your browser using localStorage and sessionStorage. 
              This data never leaves your device unless you explicitly share it.
            </p>
          </div>

          <div>
            <h4 className="font-semibold mb-2">Security Measures</h4>
            <p className="text-sm text-reddit-600 dark:text-dark-textMuted mb-2">
              We implement industry-standard security measures to protect your information:
            </p>
            <ul className="list-disc list-inside space-y-1 text-sm pl-4">
              <li>HTTPS encryption for all communications</li>
              <li>Secure data transmission protocols</li>
              <li>Regular security audits</li>
              <li>Access controls and authentication</li>
              <li>Monitoring for suspicious activity</li>
            </ul>
          </div>

          <div className="bg-reddit-50 dark:bg-dark-border p-4 rounded-lg">
            <h4 className="font-semibold mb-2 flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-reddit-orange" />
              Important Note
            </h4>
            <p className="text-sm">
              While we strive to protect your information, no method of transmission over the internet or electronic 
              storage is 100% secure. We cannot guarantee absolute security.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Your Rights */}
      <Card>
        <CardHeader>
          <CardTitle>Your Privacy Rights</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p>You have the following rights regarding your personal information:</p>
          
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-reddit-orange text-white rounded-full flex items-center justify-center flex-shrink-0 font-bold text-sm">
                1
              </div>
              <div>
                <h4 className="font-semibold">Access</h4>
                <p className="text-sm text-reddit-600 dark:text-dark-textMuted">
                  Request access to your personal information we hold
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-reddit-orange text-white rounded-full flex items-center justify-center flex-shrink-0 font-bold text-sm">
                2
              </div>
              <div>
                <h4 className="font-semibold">Correction</h4>
                <p className="text-sm text-reddit-600 dark:text-dark-textMuted">
                  Update or correct inaccurate information
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-reddit-orange text-white rounded-full flex items-center justify-center flex-shrink-0 font-bold text-sm">
                3
              </div>
              <div>
                <h4 className="font-semibold">Deletion</h4>
                <p className="text-sm text-reddit-600 dark:text-dark-textMuted">
                  Request deletion of your personal information
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-reddit-orange text-white rounded-full flex items-center justify-center flex-shrink-0 font-bold text-sm">
                4
              </div>
              <div>
                <h4 className="font-semibold">Export</h4>
                <p className="text-sm text-reddit-600 dark:text-dark-textMuted">
                  Export your data in a portable format
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-reddit-orange text-white rounded-full flex items-center justify-center flex-shrink-0 font-bold text-sm">
                5
              </div>
              <div>
                <h4 className="font-semibold">Opt-out</h4>
                <p className="text-sm text-reddit-600 dark:text-dark-textMuted">
                  Opt-out of certain data collection practices
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Cookies and Tracking */}
      <Card>
        <CardHeader>
          <CardTitle>Cookies and Tracking Technologies</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-reddit-600 dark:text-dark-textMuted">
            Trendy uses minimal tracking technologies. We primarily use localStorage for storing your preferences locally.
          </p>

          <div className="bg-reddit-50 dark:bg-dark-border p-4 rounded-lg">
            <h4 className="font-semibold mb-2">What We Use</h4>
            <ul className="list-disc list-inside space-y-1 text-sm">
              <li><strong>LocalStorage:</strong> Store your preferences, favorites, and settings</li>
              <li><strong>SessionStorage:</strong> Temporary data during your browsing session</li>
              <li><strong>Essential Cookies:</strong> Necessary for the website to function properly</li>
            </ul>
          </div>

          <p className="text-sm text-reddit-600 dark:text-dark-textMuted">
            You can clear this data at any time through your browser settings or through the Settings page in Trendy.
          </p>
        </CardContent>
      </Card>

      {/* Third-Party Services */}
      <Card>
        <CardHeader>
          <CardTitle>Third-Party Services</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p>Trendy integrates with third-party APIs to provide trending topic data:</p>
          
          <ul className="list-disc list-inside space-y-2 text-sm pl-4">
            <li><strong>Twitter API:</strong> For Twitter trending topics</li>
            <li><strong>Reddit API:</strong> For Reddit trending topics</li>
            <li><strong>Google Trends API:</strong> For Google search trends</li>
          </ul>

          <p className="text-sm text-reddit-600 dark:text-dark-textMuted">
            Please note that these third-party services have their own privacy policies. We encourage you to review 
            their policies to understand how they handle data.
          </p>
        </CardContent>
      </Card>

      {/* Children's Privacy */}
      <Card>
        <CardHeader>
          <CardTitle>Children's Privacy</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-reddit-600 dark:text-dark-textMuted mb-3">
            Trendy is not intended for children under the age of 13. We do not knowingly collect personal information 
            from children under 13. If you are a parent or guardian and believe your child has provided us with personal 
            information, please contact us so we can delete it.
          </p>
        </CardContent>
      </Card>

      {/* Changes to Policy */}
      <Card>
        <CardHeader>
          <CardTitle>Changes to This Policy</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-reddit-600 dark:text-dark-textMuted mb-3">
            We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new 
            Privacy Policy on this page and updating the "Last Updated" date.
          </p>
          <p className="text-sm text-reddit-600 dark:text-dark-textMuted">
            We encourage you to review this Privacy Policy periodically for any changes. Your continued use of Trendy 
            after any modifications indicates your acceptance of the updated policy.
          </p>
        </CardContent>
      </Card>

      {/* Contact */}
      <Card>
        <CardHeader>
          <CardTitle>Contact Us</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-reddit-600 dark:text-dark-textMuted mb-4">
            If you have any questions about this Privacy Policy or our data practices, please contact us:
          </p>
          <div className="bg-reddit-50 dark:bg-dark-border p-4 rounded-lg">
            <p className="text-sm"><strong>Email:</strong> trendy@gmail.com</p>
            <p className="text-sm"><strong>GitHub:</strong> github.com/julzlalu2224/Trendy</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PrivacyPolicy;
